import { Fragment, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { PageHeader } from "../../../components/AthenaPricingEngine/page-header";
import { Panel } from "../../../components/AthenaPricingEngine/panel";
import { FilterBar } from "../../../components/AthenaPricingEngine/filter-bar";
import { TrendLineChart } from "../../../components/AthenaPricingEngine/charts/trend-line-chart";
import { Skeleton } from "../../../ui/skeleton";
import { Button } from "../../../ui/button";
import { useIndustries, useOffices, useRatings } from "../lib/api/reference";
import { useTicketSize } from "../lib/ticket-size";
import {
  ALL_RATINGS_LABEL,
  useAnnualizedLoss,
  downloadAnnualizedLossContracts,
  type AnnualizedLossIndustryGroupRow,
  type AnnualizedLossIndustryRow,
  type AnnualizedLossOfficeLeafRow,
  type AnnualizedLossOfficeRow,
  type AnnualizedLossRatingGroupRow,
  type AnnualizedLossRow,
} from "../lib/api/annualized-loss";

const ALL_OFFICES_LABEL = "All Offices";
const ALL_INDUSTRIES_LABEL = "All Industry";

const TABLE_HEADERS = [
  "",
  "Credit Rating / Office / Industry",
  "Matured Contracts",
  "Financed Amount",
  "Lifetime Loss",
  "Weighted Average Life",
  "Loan to Life Loss %",
  "Annualized Loss %",
];

const TABLE_HEADERS_BY_INDUSTRY = [
  "",
  "Industry / Rating / Office",
  "Matured Contracts",
  "Financed Amount",
  "Lifetime Loss",
  "Weighted Average Life",
  "Loan to Life Loss %",
  "Annualized Loss %",
];

const METRIC_CSV_HEADERS = [
  "Matured Contracts",
  "Financed Amount",
  "Lifetime Loss",
  "Weighted Average Life",
  "Loan to Life Loss %",
  "Annualized Loss %",
];

const RATING_CSV_HEADERS = ["Rating", "Office", "Industry", ...METRIC_CSV_HEADERS];
const INDUSTRY_CSV_HEADERS = ["Industry", "Rating", "Office", ...METRIC_CSV_HEADERS];

// Anchor the two summary cards that drill into the by-rating table below
// scroll to, rather than navigate to -- the data they summarize lives on this
// same page, not a separate route.
const RATING_TABLE_ID = "annualized-loss-by-rating";

const INTERACTIVE_CARD_CLASS =
  "border border-border bg-card p-5 text-left transition-colors hover:border-primary/60 hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary";

function scrollToRatingTable() {
  document.getElementById(RATING_TABLE_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

type CsvRow = Record<string, string | number>;

function metricCsvFields(row: AnnualizedLossIndustryRow): CsvRow {
  return {
    "Matured Contracts": row.maturedContracts,
    "Financed Amount": row.financedAmount,
    "Lifetime Loss": row.lifetimeLoss,
    "Weighted Average Life": row.walYears ?? "",
    "Loan to Life Loss %": row.loanToLifeLossPct ?? "",
    "Annualized Loss %": row.annualizedLossPct ?? "",
  };
}

/** Rating -> Office -> Industry, matching Table 1's own on-screen nesting --
 *  one CSV row per tree node, with only that node's own hierarchy column
 *  filled in (descendant columns blank), the same convention the source
 *  pivot-table mockup uses. Walks the full tree regardless of which rows are
 *  currently expanded on screen -- a CSV export is a full data dump, not a
 *  snapshot of what's visible. */
function flattenRatingTree(rows: AnnualizedLossRow[]): CsvRow[] {
  const out: CsvRow[] = [];
  for (const ratingRow of rows) {
    out.push({ Rating: ratingRow.label, Office: "", Industry: "", ...metricCsvFields(ratingRow) });
    for (const officeRow of ratingRow.offices) {
      out.push({ Rating: "", Office: officeRow.label, Industry: "", ...metricCsvFields(officeRow) });
      for (const industryRow of officeRow.industries) {
        out.push({ Rating: "", Office: "", Industry: industryRow.label, ...metricCsvFields(industryRow) });
      }
    }
  }
  return out;
}

/** Industry -> Rating -> Office, matching Table 2's own on-screen nesting. */
function flattenIndustryTree(rows: AnnualizedLossIndustryGroupRow[]): CsvRow[] {
  const out: CsvRow[] = [];
  for (const industryRow of rows) {
    out.push({ Industry: industryRow.label, Rating: "", Office: "", ...metricCsvFields(industryRow) });
    for (const ratingRow of industryRow.ratings) {
      out.push({ Industry: "", Rating: ratingRow.label, Office: "", ...metricCsvFields(ratingRow) });
      for (const officeRow of ratingRow.offices) {
        out.push({ Industry: "", Rating: "", Office: officeRow.label, ...metricCsvFields(officeRow) });
      }
    }
  }
  return out;
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /["\n,]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** No client-side CSV precedent exists in this codebase -- the one real
 *  download precedent (scenario.ts's downloadScenarioPledgeProposal) fetches
 *  a server-generated blob, needed there because the CSV itself comes from
 *  the backend. Here the full tree is already in hand from the page's own
 *  query, so this builds and downloads the CSV directly in the browser. */
function downloadCsv(filename: string, headers: string[], rows: CsvRow[]) {
  const lines = [headers.join(",")];
  for (const row of rows) {
    lines.push(headers.map((h) => csvCell(row[h] ?? "")).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function RawDataButton({
  industry,
  officeNumber,
  rating,
}: {
  industry?: string;
  officeNumber?: number;
  rating?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const { ticket } = useTicketSize();
  return (
    <div className="flex items-center gap-2">
      {failed ? <span className="text-xs text-destructive">Download failed</span> : null}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setFailed(false);
          try {
            await downloadAnnualizedLossContracts(industry, officeNumber, rating, ticket);
          } catch {
            setFailed(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        <Download className="size-3.5" />
        {busy ? "Downloading…" : "Download Raw Data"}
      </Button>
    </div>
  );
}

function DownloadCsvButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" variant="outline" size="sm" onClick={onClick}>
      <Download className="size-3.5" />
      Download CSV
    </Button>
  );
}

function pct(value: number | null) {
  return value == null ? "—" : `${value.toFixed(2)}%`;
}

function years(value: number | null) {
  return value == null ? "—" : `${value.toFixed(2)} yrs`;
}

function dollars(value: number) {
  return `$${Math.round(value).toLocaleString()}`;
}

function dollarsM(value: number) {
  return `$${(value / 1_000_000).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}M`;
}

/** Same bordered-square chevron toggle Active Policy's own drill-down uses,
 *  so the interaction reads the same way everywhere it appears in this product. */
function ExpandToggle({ expanded, onClick, label }: { expanded: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
      aria-label={expanded ? `Collapse ${label}` : `Expand ${label}`}
      onClick={onClick}
    >
      {expanded ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
    </button>
  );
}

/** The four metric cells shared by every level of the tree (rating, office,
 *  industry all carry the same fields). */
function MetricCells({ row }: { row: AnnualizedLossIndustryRow }) {
  return (
    <>
      <td className="px-3 py-2.5 tabular-nums text-foreground">{row.maturedContracts.toLocaleString()}</td>
      <td className="px-3 py-2.5 tabular-nums text-foreground">{dollars(row.financedAmount)}</td>
      <td className="px-3 py-2.5 tabular-nums text-foreground">{dollars(row.lifetimeLoss)}</td>
      <td className="px-3 py-2.5 tabular-nums text-foreground">{years(row.walYears)}</td>
      <td className="px-3 py-2.5 tabular-nums text-foreground">{pct(row.loanToLifeLossPct)}</td>
      <td className="px-3 py-2.5 tabular-nums font-semibold text-primary">{pct(row.annualizedLossPct)}</td>
    </>
  );
}

function AnnualizedLossTable({ rows }: { rows: AnnualizedLossRow[] }) {
  const [expandedRatings, setExpandedRatings] = useState<Record<string, boolean>>({});
  const [expandedOffices, setExpandedOffices] = useState<Record<string, boolean>>({});

  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[960px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {TABLE_HEADERS.map((head, i) => (
              <th
                key={`${head}-${i}`}
                className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((ratingRow) => {
            const ratingExpanded = !!expandedRatings[ratingRow.key];
            return (
              <Fragment key={ratingRow.key}>
                <tr
                  className={
                    ratingRow.key === "all"
                      ? "border-b border-border/60 bg-secondary/30 font-medium last:border-b-0"
                      : "border-b border-border/60 last:border-b-0"
                  }
                >
                  <td className="px-2 py-2.5">
                    {ratingRow.offices.length ? (
                      <ExpandToggle
                        expanded={ratingExpanded}
                        label={`${ratingRow.label}'s offices`}
                        onClick={() =>
                          setExpandedRatings((e) => ({ ...e, [ratingRow.key]: !e[ratingRow.key] }))
                        }
                      />
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-foreground">{ratingRow.label}</td>
                  <MetricCells row={ratingRow} />
                </tr>

                {ratingExpanded &&
                  ratingRow.offices.map((officeRow: AnnualizedLossOfficeRow) => {
                    const officeKey = `${ratingRow.key}|${officeRow.key}`;
                    const officeExpanded = !!expandedOffices[officeKey];
                    return (
                      <Fragment key={officeRow.key}>
                        <tr className="border-b border-border/60 last:border-b-0">
                          <td className="px-2 py-2.5 pl-6">
                            {officeRow.industries.length ? (
                              <ExpandToggle
                                expanded={officeExpanded}
                                label={`${officeRow.label} industries`}
                                onClick={() =>
                                  setExpandedOffices((e) => ({ ...e, [officeKey]: !e[officeKey] }))
                                }
                              />
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5 pl-6 text-foreground/90">{officeRow.label}</td>
                          <MetricCells row={officeRow} />
                        </tr>

                        {officeExpanded &&
                          officeRow.industries.map((industryRow) => (
                            <tr
                              key={industryRow.key}
                              className="border-b border-border/60 bg-secondary/20 last:border-b-0"
                            >
                              <td className="px-2 py-2.5" />
                              <td className="px-3 py-2.5 pl-12 text-foreground/90">{industryRow.label}</td>
                              <MetricCells row={industryRow} />
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Same tree-table shape as AnnualizedLossTable, roles reordered --
 *  Industry (top) -> Rating -> Office. Kept as its own parallel component
 *  rather than a generic/parameterized one, the same precedent
 *  manage-targets-panels.tsx already sets with ActivePolicyTable /
 *  ActivePolicyTableByIndustry for this exact office-led vs industry-led
 *  situation. */
function AnnualizedLossTableByIndustry({ rows }: { rows: AnnualizedLossIndustryGroupRow[] }) {
  const [expandedIndustries, setExpandedIndustries] = useState<Record<string, boolean>>({});
  const [expandedRatings, setExpandedRatings] = useState<Record<string, boolean>>({});

  return (
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[960px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {TABLE_HEADERS_BY_INDUSTRY.map((head, i) => (
              <th
                key={`${head}-${i}`}
                className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((industryRow) => {
            const industryExpanded = !!expandedIndustries[industryRow.key];
            return (
              <Fragment key={industryRow.key}>
                <tr
                  className={
                    industryRow.key === "all"
                      ? "border-b border-border/60 bg-secondary/30 font-medium last:border-b-0"
                      : "border-b border-border/60 last:border-b-0"
                  }
                >
                  <td className="px-2 py-2.5">
                    {industryRow.ratings.length ? (
                      <ExpandToggle
                        expanded={industryExpanded}
                        label={`${industryRow.label}'s ratings`}
                        onClick={() =>
                          setExpandedIndustries((e) => ({ ...e, [industryRow.key]: !e[industryRow.key] }))
                        }
                      />
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 font-medium text-foreground">{industryRow.label}</td>
                  <MetricCells row={industryRow} />
                </tr>

                {industryExpanded &&
                  industryRow.ratings.map((ratingRow: AnnualizedLossRatingGroupRow) => {
                    const ratingKey = `${industryRow.key}|${ratingRow.key}`;
                    const ratingExpanded = !!expandedRatings[ratingKey];
                    return (
                      <Fragment key={ratingRow.key}>
                        <tr className="border-b border-border/60 last:border-b-0">
                          <td className="px-2 py-2.5 pl-6">
                            {ratingRow.offices.length ? (
                              <ExpandToggle
                                expanded={ratingExpanded}
                                label={`${ratingRow.label} offices`}
                                onClick={() =>
                                  setExpandedRatings((e) => ({ ...e, [ratingKey]: !e[ratingKey] }))
                                }
                              />
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5 pl-6 text-foreground/90">{ratingRow.label}</td>
                          <MetricCells row={ratingRow} />
                        </tr>

                        {ratingExpanded &&
                          ratingRow.offices.map((officeRow: AnnualizedLossOfficeLeafRow) => (
                            <tr
                              key={officeRow.key}
                              className="border-b border-border/60 bg-secondary/20 last:border-b-0"
                            >
                              <td className="px-2 py-2.5" />
                              <td className="px-3 py-2.5 pl-12 text-foreground/90">{officeRow.label}</td>
                              <MetricCells row={officeRow} />
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function AnnualizedLoss() {
  const [searchParams, setSearchParams] = useSearchParams();
  const office = searchParams.get("office") ?? undefined;
  const industry = searchParams.get("industry") ?? undefined;
  const searchRating = searchParams.get("rating") ?? undefined;

  const officesQuery = useOffices();
  const offices = officesQuery.data ?? [];
  const industriesQuery = useIndustries();
  const industries = industriesQuery.data ?? [];
  const ratingsQuery = useRatings();
  const ratingOptions = ratingsQuery.data ?? [];
  const rating = searchRating && ratingOptions.includes(searchRating) ? searchRating : undefined;

  // annualized_loss_calibiration(_trend) keys on office_number, not name --
  // same resolution Weighted Average Life's own page does before calling its API.
  const officeNumber = office
    ? offices.find((o) => o.officeName === office)?.officeNumber
    : undefined;

  const query = useAnnualizedLoss(industry, officeNumber, rating);

  // "All Ratings" is the whole-filtered-portfolio rollup row; when a single
  // rating is pinned, get_rows omits that redundant row and returns just the
  // one row instead, which IS the whole answer -- see the backend's own
  // get_rows docstring.
  const totalsRow = query.data?.rows.find((r) => r.key === "all") ?? query.data?.rows[0];

  const trendData = useMemo(
    () =>
      (query.data?.trend ?? []).map((point) => ({
        month: point.fileDate,
        annualizedLoss: point.annualizedLossPct,
        lifetimeLossM: point.lifetimeLoss / 1_000_000,
      })),
    [query.data],
  );

  function setFilterParam(key: "office" | "industry" | "rating", value: string, allLabel: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === allLabel) next.delete(key);
      else next.set(key, value);
      return next;
    });
  }

  return (
    <div>
      <PageHeader
        title="Annualized Loss"
        right={<RawDataButton industry={industry} officeNumber={officeNumber} rating={rating} />}
      />

      <FilterBar
        className="mt-8 mb-5"
        filters={[
          {
            id: "office",
            label: "Office Name",
            value: office ?? ALL_OFFICES_LABEL,
            options: [ALL_OFFICES_LABEL, ...offices.map((o) => o.officeName)],
            onChange: (value) => setFilterParam("office", value, ALL_OFFICES_LABEL),
          },
          {
            id: "industry",
            label: "Industry",
            value: industry ?? ALL_INDUSTRIES_LABEL,
            options: [ALL_INDUSTRIES_LABEL, ...industries],
            onChange: (value) => setFilterParam("industry", value, ALL_INDUSTRIES_LABEL),
          },
          {
            id: "rating",
            label: "Rating",
            value: rating ?? ALL_RATINGS_LABEL,
            options: [ALL_RATINGS_LABEL, ...ratingOptions],
            onChange: (value) => setFilterParam("rating", value, ALL_RATINGS_LABEL),
          },
        ]}
      />

      {query.isError ? null : query.isLoading || !totalsRow ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <button type="button" onClick={scrollToRatingTable} className={INTERACTIVE_CARD_CLASS}>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Matured Contracts
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
              {totalsRow.maturedContracts.toLocaleString()}
            </p>
          </button>
          <button type="button" onClick={scrollToRatingTable} className={INTERACTIVE_CARD_CLASS}>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Financed Amount
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
              {dollarsM(totalsRow.financedAmount)}
            </p>
          </button>
          <Link to="/risk-pricing-engine/weighted-average-life" className={INTERACTIVE_CARD_CLASS}>
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Weighted Average Life
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
              {years(totalsRow.walYears)}
            </p>
          </Link>
          <div className="border border-border bg-card p-5">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Annualized Loss
            </p>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
              {pct(totalsRow.annualizedLossPct)}
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel title="Annualized Loss Trend (%)">
          {query.isError ? (
            <div
              role="alert"
              className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              Could not load the annualized loss trend.
            </div>
          ) : query.isLoading || !query.data ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <TrendLineChart
              data={trendData}
              series={[{ dataKey: "annualizedLoss", name: "Annualized Loss", color: "var(--athena-yellow)" }]}
            />
          )}
        </Panel>

        <Panel title="Lifetime Loss Trends ($)">
          {query.isError ? (
            <div
              role="alert"
              className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
            >
              Could not load the lifetime loss trend.
            </div>
          ) : query.isLoading || !query.data ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <TrendLineChart
              data={trendData}
              series={[{ dataKey: "lifetimeLossM", name: "Lifetime Loss", color: "hsl(var(--chart-2))" }]}
              prefix="$"
              suffix="M"
            />
          )}
        </Panel>
      </div>

      <Panel
        id={RATING_TABLE_ID}
        className="mt-8 scroll-mt-[200px]"
        title="Annualized Loss by Rating"
        description="Expand a rating to drill into Office, then into Industry."
        right={
          query.data ? (
            <DownloadCsvButton
              onClick={() =>
                downloadCsv(
                  "annualized-loss-by-rating.csv",
                  RATING_CSV_HEADERS,
                  flattenRatingTree(query.data.rows),
                )
              }
              />
          ) : null
        }
      >
        {query.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load annualized loss.
          </div>
        ) : query.isLoading || !query.data ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <AnnualizedLossTable rows={query.data.rows} />
        )}
      </Panel>

      <Panel
        className="mt-8"
        title="Annualized Loss by Industry"
        description="Expand an industry to drill into Rating, then into Office."
        right={
          query.data ? (
            <DownloadCsvButton
              onClick={() =>
                downloadCsv(
                  "annualized-loss-by-industry.csv",
                  INDUSTRY_CSV_HEADERS,
                  flattenIndustryTree(query.data.rowsByIndustry),
                )
              }
              />
          ) : null
        }
      >
        {query.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load annualized loss.
          </div>
        ) : query.isLoading || !query.data ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <AnnualizedLossTableByIndustry rows={query.data.rowsByIndustry} />
        )}
      </Panel>
    </div>
  );
}
